/** @jsxImportSource @emotion/react */
import {
  Button,
  Form,
  Stack,
  Text,
  useBasisForm,
  useFieldArray,
  useTheme,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { getRegional } from "library/getRegional";
import { usePageTracking } from "react-event-tracker";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import { getNumberOfSteps } from "../reducer/hardshipSelectors";
import { ActionTypes, YourAccountsState } from "../reducer/types";
import { AccountBox } from "./AccountBox";

export const YourAccounts = () => {
  const { state, dispatch } = useHardshipApplicationContext();
  const numberOfSteps = getNumberOfSteps(state);

  const { navigate, goBack } = useNav();

  const { methods } = useBasisForm<YourAccountsState>({
    defaultValues: state.yourAccounts,
  });
  const theme = useTheme();

  const { control } = methods;

  const onSubmit = (values: YourAccountsState) => {
    dispatch({ type: ActionTypes.COMPLETE_YOUR_ACCOUNTS, ...values });
    navigate("reasons-for-applying");
  };

  const { fields, remove, append } = useFieldArray({
    control,
    name: "accounts",
  });

  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "hardship",
      siteSubSection: "accounts",
      pageTitle: "Your accounts",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  const region = getRegional("code");
  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8" width="100%" width-sm="736">
            <Stack gap="4">
              <Text>
                <strong>Step 2 of {numberOfSteps}</strong>
              </Text>
              <Text as="h2" textStyle="heading4">
                Your accounts
              </Text>
            </Stack>
            <Text color="grey.t75">
              <strong>Which accounts do you need Hardship Care for?</strong>
              <br />
              You can add multiple accounts if you need to.
            </Text>
            {fields.map((value, index) => (
              <AccountBox
                index={index}
                account={value}
                region={region}
                onRemove={remove}
              />
            ))}
            {fields.length < 10 && (
              <button
                type="button"
                css={{
                  appearance: "none",
                  border: "none",
                  color: theme.colors.highlight.blue.t100,
                  ...theme.getTextStyleCSS("subtitle2"),
                }}
                onClick={() =>
                  append({
                    accountType: "",
                    accountNumber: "",
                    monthlyRepayments: "",
                  })
                }
              >
                <strong>
                  <span aria-hidden>+ </span>Add another account
                </strong>
              </button>
            )}
            <FormActions>
              <FormActions.Primary width="172">
                <Button variant="primary" type="submit">
                  Next
                </Button>
              </FormActions.Primary>
              <FormActions.Secondary width="172">
                <Button variant="secondary" onClick={goBack}>
                  Back
                </Button>
              </FormActions.Secondary>
            </FormActions>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
